import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DealerBadge } from "@/components/DealerBadge";
import { MapPin, Package, Calendar, Edit } from "lucide-react";
import { toast } from "sonner";

export default function SellerProfile() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");

  const load = () => api.get(`/sellers/${id}`).then((r) => { setProfile(r.data); setBio(r.data.bio || ""); setLocation(r.data.location || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveProfile = async () => {
    const r = await api.put("/profile", { bio, location });
    setUser(r.data);
    toast.success("Profile updated");
    load();
  };

  if (!profile) return <div className="max-w-[1500px] mx-auto px-6 py-10"><div className="h-40 bg-[#141414] border border-border rounded-sm animate-pulse" /></div>;
  const isMe = user && user.user_id === id;
  const joined = new Date(profile.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" });

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
      <div className="bg-[#141414] border border-border rounded-sm p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <Avatar className="w-20 h-20 border border-border"><AvatarImage src={profile.picture} /><AvatarFallback className="bg-secondary text-2xl">{profile.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 data-testid="seller-name" className="font-heading font-900 text-2xl uppercase tracking-tight">{profile.name}</h1>
            {profile.is_dealer && <DealerBadge size="lg" />}
          </div>
          {profile.bio && <p className="text-sm text-muted-foreground mt-1 max-w-xl">{profile.bio}</p>}
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
            {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.location}</span>}
            <span className="flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {profile.listing_count} active listings</span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Joined {joined}</span>
          </div>
        </div>
        {isMe && (
          <Dialog>
            <DialogTrigger asChild><Button data-testid="edit-profile-btn" variant="outline" className="border-border bg-transparent rounded-sm gap-1.5"><Edit className="w-4 h-4" /> Edit Profile</Button></DialogTrigger>
            <DialogContent className="bg-[#141414] border-border">
              <DialogHeader><DialogTitle className="font-heading uppercase">Edit Profile</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Textarea data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell buyers about yourself / your shop" className="bg-[#0A0A0A] border-border rounded-sm" />
                <Input data-testid="profile-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State" className="bg-[#0A0A0A] border-border rounded-sm" />
              </div>
              <DialogFooter><Button data-testid="profile-save-btn" onClick={saveProfile} className="bg-blaze hover:bg-blaze-hover rounded-sm font-bold">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <h2 className="font-heading font-800 text-lg uppercase tracking-tight mt-8 mb-4">Listings</h2>
      {profile.listings.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground">No active listings.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {profile.listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      )}
    </div>
  );
}
